<script setup lang="ts">
import { $t } from "@/i18n";
import router from "@/router";
import { useDefaultStore } from "@/store";
import { useRoute } from "vue-router";

interface INavItem {
	name: string;
	label: string;
	routeName: string;
}

const route = useRoute();
const store = useDefaultStore();
const activeTab = ref<any>();
const dropdownElement = ref<HTMLElement | null>(null);
const navObj = ref<Record<string, INavItem>>({
	aboutUs: {
		name: "AboutPage",
		label: "app.about.title",
		routeName: "AboutPage",
	},
	expertise: {
		name: "ExpertisePage",
		label: "app.legalExpertise.mainTitle",
		routeName: "ExpertisePage",
	},
	references: {
		name: "LegalRefernces",
		label: "app.referencesTitle",
		routeName: "LegalReferences",
	},
	financing: {
		name: "FinancingPage",
		label: "app.legalFinancing.title",
		routeName: "FinancingPage",
	},
	testimonials: {
		name: "LegalTestimonials",
		label: "app.legalTestimonials.navLabel",
		routeName: "LegalTestimonials",
	},
	academy: {
		name: "LegalAcademy",
		label: "app.academyTitle",
		routeName: "LegalAcademy",
	},
});

// Set nav list

async function setNavList(path: string) {
	if (path.startsWith("/pravna")) {
		navObj.value = {
			aboutUs: {
				name: "AboutPage",
				label: "app.about.title",
				routeName: "AboutPage",
			},
			expertise: {
				name: "ExpertisePage",
				label: "app.legalExpertise.mainTitle",
				routeName: "ExpertisePage",
			},
			references: {
				name: "LegalRefernces",
				label: "app.referencesTitle",
				routeName: "LegalReferences",
			},
			financing: {
				name: "FinancingPage",
				label: "app.legalFinancing.title",
				routeName: "FinancingPage",
			},
			testimonials: {
				name: "LegalTestimonials",
				label: "app.legalTestimonials.navLabel",
				routeName: "LegalTestimonials",
			},
			academy: {
				name: "LegalAcademy",
				label: "app.academyTitle",
				routeName: "LegalAcademy",
			},
		};
	} else if (path.startsWith("/fizicka") || path.startsWith("/blog")) {
		navObj.value = {
			aboutUs: {
				name: "AboutPage",
				label: "app.about.title",
				routeName: "AboutPage",
			},
			fearsAndMyths: {
				name: "FearsMythsPage",
				label: "app.fearsAndMyths.title",
				routeName: "",
			},
			calculator: {
				name: "CalculatorPage",
				label: "app.calculator.title",
				routeName: "CalculatorPage",
			},
			testimonials: {
				name: "RegularTestimonials",
				label: "app.testimonialsTitle",
				routeName: "RegularTestimonials",
			},
			references: {
				name: "ReagularRefernces",
				label: "app.referencesTitle",
				routeName: "RegularReferences",
			},
			academy: {
				name: "RegularAcademy",
				label: "app.academyTitle",
				routeName: "RegularAcademy",
			},
		};
	} else {
		// await router.push({
		// 	name: "LegalPage",
		// });
	}
}

const navList = computed(() => {
	const navArray = Object.values(navObj.value);
	return navArray.map((item) => ({
		name: item.name,
		label: $t(item.label),
		routeName: item.routeName,
	}));
});

async function onClickedMenuItem(item: (typeof navList.value)[0]) {
	store.isMobileToggled = false;

	await router.push({
		name: item.routeName,
	});
}

// Inicialize active tab

const initializeActiveTab = () => {
	activeTab.value = findActiveTab(route.path);
};

const findActiveTab = (path: string) => {
	if (
		!store.isReference &&
		(path.startsWith("/pravna/blog") ||
			path.startsWith("/blog") ||
			path.startsWith("/fizicka-lica/akademija") ||
			path.startsWith("/pravna-lica/akademija"))
	) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.academy.routeName,
		);
	} else if (path == "/o-nama") {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.aboutUs.routeName,
		);
	} else if (
		path.startsWith("/pravna-lica/reference") ||
		path.startsWith("/fizicka-lica/reference") ||
		store.isReference
	) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.references.routeName,
		);
	} else if (
		path.startsWith("/pravna-lica/video-testimonials") ||
		path.startsWith("/fizicka-lica/video-testimonials")
	) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.testimonials.routeName,
		);
	} else if (path.startsWith("/pravna-lica/strucnost")) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.expertise.routeName,
		);
	} else if (path.startsWith("/pravna-lica/finansiranje")) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.financing.routeName,
		);
	} else if (path.startsWith("/fizicka-lica/kalkulator")) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.calculator.routeName,
		);
	} else if (
		path.startsWith("/fizicka-lica/strahovi-mitovi") ||
		path.startsWith("/fizicka-lica/zasto-investirati-u-solarnu-elektranu") ||
		path.startsWith("/fizicka-lica/kako-funkcionišu-solarni-paneli") ||
		path.startsWith("/fizicka-lica/proces-ugradnje-instalacije-solarnih-panela")
	) {
		return navList.value.find(
			(tab) => tab.routeName === navObj.value.fearsAndMyths.routeName,
		);
	}
};

watch(
	() => route.path,
	async (newPath, oldPath) => {
		await setNavList(newPath);
		activeTab.value = findActiveTab(newPath);
		await showDropdown();
	},
);

async function showDropdown() {
	const navLabels = document.querySelectorAll(".label.name");
	const uniTabsEl = document.querySelectorAll(".uni-tabs");
	if (
		navLabels &&
		navLabels.length > 3 &&
		(navLabels[3] as HTMLElement).innerText.includes("Strahovi i mitovi") &&
		uniTabsEl.length > 1
	) {
		(uniTabsEl[1] as HTMLElement).classList.add("uni-tabs-dd");
	} else {
		(uniTabsEl[1] as HTMLElement).classList.remove("uni-tabs-dd");
	}
}

onMounted(async () => {
	await setNavList(route.path);
	initializeActiveTab();
	await showDropdown();
});

const filteredNavList = computed(() =>
	navList.value.filter((navItem) => navItem.label !== "Strahovi i mitovi"),
);
</script>

<template lang="pug">
.header-bar
	.left-side
		router-link.logo-wrapper(
			to="/pravna-lica",
			draggable="false",
			aria-label="Back"
		)
			img.logo(
				src="@/assets/image/logo.svg",
				draggable="false",
				alt="logo",
				loading="lazy"
			)
	.center
		.menu
			uni-tabs(
				:tabs="navList",
				:activeTab="activeTab",
				:can-close-tabs="false",
				:modern-ui="true",
				:is-top-line="false"
			)
	.right-side
		a.home-page-full-button.call-us-button(
			href="tel:+38163267266",
			aria-label="Call us",
			@click="store.trackClickCall()"
		) 
			span {{ $t("app.callUs").toUpperCase() }}
			span.number 063/267-266
	.mobile-menu-wrap
		burger-menu-btn(
			:isMobileHeaderToggled="store.isMobileToggled",
			@toggle-burger="store.isMobileToggled = !store.isMobileToggled"
		)
		transition(name="slide-fade")
			header-mobile(
				v-if="store.isMobileToggled",
				:navList="filteredNavList",
				@click-menu-item="onClickedMenuItem",
				@close="store.isMobileToggled = false"
			)
</template>

<style lang="scss" scoped>
.header-bar {
	display: flex;
	gap: 10px;
	background-color: $text-color-lighter;
	height: 60px;
	min-height: 60px;
	padding: 0 24px;
	top: 0;
	z-index: 10;
	box-sizing: content-box; // Because of the border-bottom
	// border: 1px solid red;

	.left-side,
	.center,
	.right-side {
		flex: 1;
		display: flex;
		align-items: center;
		gap: 10px;
		white-space: nowrap;
	}

	.center {
		font-weight: 500;
		align-items: center;
		padding: 0;
	}

	.logo-wrapper {
		width: 140px;
		height: 35px;

		img {
			width: 100%;
		}
	}

	.right-side {
		display: none;
		justify-content: flex-end;
		gap: 15px;

		.home-page-full-button {
			padding: 12px 24px !important;
			display: flex;
			flex-direction: column;

			span {
				font-size: 12px;
			}

			// .number{
			// 	font-size: 12px;
			// }
			// display: none;

			// &.phone{
			// 	display: flex;
			// }
		}
	}

	.menu {
		display: none;
		gap: 10px;

		:deep() {
			.uni-tabs-wrap {
				overflow: visible;
				.uni-tabs {
					gap: 1vw;

					.tab {
						.row {
							.content-part {
								.name,
								.label {
									font-family: "Galano Grotesque", sans-serif;
									font-size: 13px;
									font-weight: 500;
									line-height: 17px;
								}
							}
						}

						&.tab-active {
							.row {
								.name,
								.label {
									color: $app-accent-color1;
								}
							}
						}
					}
				}

				.line-wrapper {
					margin-top: unset;

					.line {
						// height: 5px;
						height: 3px;
						background-color: $app-accent-color1;
					}
				}
				.uni-tabs-dd > :nth-last-child(5) {
					/* stylelint-disable-next-line no-descending-specificity */
					.row {
						.content-part {
							.name,
							.label {
								width: 124px;
								text-overflow: unset;
								overflow: unset;
								white-space: break-spaces;
								background-image: url("@/assets/image/header/drop-down-icon.svg");
								background-repeat: no-repeat;
								background-position: right;
								background-size: 0.75rem;
								// background-size: contain;
								position: relative;

								&:hover {
									#header-dropdown {
										display: block !important;
										height: auto;
										width: calc(100% + 20px);
										position: absolute;
										top: calc(100% + 28px);
										left: -10px;
										z-index: 100;
										font-family: "Galano Grotesque", sans-serif;
										font-size: 12px;
										font-weight: 500;
										line-height: 18px;
										text-align: left;
										background-color: $text-color-lighter;
										border: 1px solid rgba(223, 223, 232, 1);

										.item {
											margin: 10px;

											&:hover {
												color: $app-accent-color1;
											}

											&.dropdown-active {
												color: $app-accent-color1;
											}
										}

										.header-dropdown-line {
											height: 1px;
											width: 80%;
											background-color: rgb(227, 227, 235);
											margin: 0 auto;
										}
									}
								}

								&::before {
									content: "";
									position: absolute;
									top: 90%;
									left: -10px;
									height: 30px;
									width: calc(100% + 20px);
									background-color: transparent;
									z-index: 101;
									// border: 1px solid red;

									&:hover {
										#header-dropdown {
											display: block !important;
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}

	.mobile-menu-wrap {
		display: flex;
	}
}

@media screen and (width >= 768px) {
	.header-bar {
		height: $nav-bottom-bar;
		min-height: $nav-bottom-bar;

		// .left-side,
		// .center,
		// .right-side {
		// }

		// .center {
		// }

		.left-side {
			.logo-wrapper {
				height: 70%;
				width: unset;

				.logo {
					width: 100%;
					height: 100%;
				}
			}
		}

		// .right-side {

		// 	.home-page-full-button {

		// 		&.phone{
		// 		}
		// 	}
		// }

		// .menu {

		// 	:deep() {
		// 		.uni-tabs-wrap {
		// 			.uni-tabs {

		// 				.tab {
		// 					.row{
		// 						.content-part{
		// 							.name,
		// 							.label {
		// 							}
		// 						}
		// 					}

		// 					&.tab-active {
		// 						.row {
		// 							.name,
		// 							.label {
		// 							}
		// 						}
		// 					}
		// 				}
		// 			}

		// 			.line-wrapper {

		// 				.line {
		// 				}
		// 			}
		// 			.uni-tabs-dd > :nth-last-child(5) {
		// 				/* stylelint-disable-next-line no-descending-specificity */
		// 				.row {
		// 					.content-part {
		// 						.name,
		// 						.label {

		// 							&:hover {
		// 								#header-dropdown {

		// 									.item {

		// 										&:hover {
		// 										}

		// 										&.dropdown-active {
		// 										}
		// 									}

		// 									.header-dropdown-line {
		// 									}
		// 								}
		// 							}

		// 							&::before {

		// 								&:hover {
		// 									#header-dropdown {
		// 									}
		// 								}
		// 							}
		// 						}
		// 					}
		// 				}
		// 			}
		// 		}
		// 	}
		// }

		// .mobile-menu-wrap {
		// }
	}
}

@media screen and (width >= 1024px) {
	.header-bar {
		padding: 0 85px;

		// .left-side,
		// .center,
		// .right-side {
		// }

		// .center {
		// }

		// .left-side{
		// 	.logo-wrapper {

		// 		.logo {
		// 		}
		// 	}
		// }

		// .right-side {

		// 	.home-page-full-button {

		// 		&.phone{
		// 		}
		// 	}
		// }

		// .menu {

		// 	:deep() {
		// 		.uni-tabs-wrap {
		// 			.uni-tabs {

		// 				.tab {
		// 					.row{
		// 						.content-part{
		// 							.name,
		// 							.label {
		// 							}
		// 						}
		// 					}

		// 					&.tab-active {
		// 						.row {
		// 							.name,
		// 							.label {
		// 							}
		// 						}
		// 					}
		// 				}
		// 			}

		// 			.line-wrapper {

		// 				.line {
		// 				}
		// 			}
		// 			.uni-tabs-dd > :nth-last-child(5) {
		// 				/* stylelint-disable-next-line no-descending-specificity */
		// 				.row {
		// 					.content-part {
		// 						.name,
		// 						.label {

		// 							&:hover {
		// 								#header-dropdown {

		// 									.item {

		// 										&:hover {
		// 										}

		// 										&.dropdown-active {
		// 										}
		// 									}

		// 									.header-dropdown-line {
		// 									}
		// 								}
		// 							}

		// 							&::before {

		// 								&:hover {
		// 									#header-dropdown {
		// 									}
		// 								}
		// 							}
		// 						}
		// 					}
		// 				}
		// 			}
		// 		}
		// 	}
		// }

		// .mobile-menu-wrap {
		// }
	}
}

@media screen and (width >= 1100px) {
	.menu {
		display: flex !important;
	}
	.right-side {
		display: flex !important;
	}

	.mobile-menu-wrap {
		display: none !important;
	}
}

@media screen and (width >= 1572px) {
	.header-bar {
		padding: 0 85px;

		.right-side {
			.home-page-full-button {
				display: flex;

				span {
					font-size: 14px;
				}

				&.phone {
					display: none;
				}
			}
		}

		.menu {
			:deep() {
				.uni-tabs-wrap {
					.uni-tabs {
						gap: 60px;

						.tab {
							.row {
								.content-part {
									.name,
									.label {
										font-size: 20px;
										line-height: 27px;
									}
								}
							}

							// &.tab-active {
							// 	.row {
							// 		.name,
							// 		.label {
							// 		}
							// 	}
							// }
						}
					}

					.line-wrapper {
						margin-top: unset !important;

						.line {
							height: 5px;
						}
					}
					.uni-tabs-dd > :nth-last-child(5) {
						/* stylelint-disable-next-line no-descending-specificity */
						.row {
							.content-part {
								.name,
								.label {
									width: 140px;
									background-size: unset;

									&:hover {
										#header-dropdown {
											top: calc(100% + 7px);
											font-size: 14px;
											line-height: 20px;

											// .item {

											// 	// &:hover {
											// 	// }

											// 	// &.dropdown-active {
											// 	// }
											// }

											// .header-dropdown-line {
											// }
										}
									}

									&::before {
										content: "";
										position: absolute;
										top: 90%;
										left: -10px;
										height: 12px;
										width: calc(100% + 20px);
										background-color: transparent;
										z-index: 101;

										// &:hover {
										// 	#header-dropdown {
										// 	}
										// }
									}
								}
							}
						}
					}
				}
			}
		}

		// .mobile-menu-wrap {
		// }
	}
}
</style>
